import { assertLinkRequestActive } from '#~/api/agent/link-request-expiry.js';
import type { LinkRequestLifetime } from '#~/api/agent/link-request-expiry.js';
import { LinkRequestLifetimeNotice, useLinkRequestLifetime } from './link-request-lifetime.jsx';
import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
import * as Wizard from '#~/agent/components/wizard/index.js';
import type { LinkRequest } from '#~/api/agent/index.js';
import {
  fetchAgentsForRoom,
  fetchRequestLink,
  linkAgent,
  listOrganizations
} from '#~/api/agent/index.js';
import { canAddSourceToRoom, useBillingLimitMonitor } from '#~/api/billing/limits.js';
import type { ApiError } from '#~/api/index.js';
import { toIdFromHex } from '#~/api/utils.js';
import { useAuth0 } from '#~/auth/index.js';
import { NotFoundError } from '#~/error/index.js';
import { logger } from '#~/logger/logger.js';
import { nats } from '#~/nats/index.js';
import { initAccount } from '#~/room/index.js';
import { Paths } from '#~/routes/index.js';
import { Anchor } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import { Loader } from '@studio/design-system/loader';
import * as Select from '@studio/design-system/select';
import { unwrap } from '@studio/js-utils/async';
import { normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import type { RoomRecord } from '@studio/models/room';
import { useAsyncEffect, useLatestFn } from '@studio/react-utils/hooks';
import type { AgentRegistryGetOrganizationResponse } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import {
  BillingRegistryGetRoomBillingInfoRequest,
  BillingRegistryPlan
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import { BillingRegistryGetBillingInfo } from '@tryiris-ai/mailroom-helper/billing';
import { noop } from 'lodash-es';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { OrganizationSelector } from './components/organization-selector.jsx';
import { Hardware } from './hardware.jsx';
import { useLinkFlowConfig } from './hooks/use-link-flow-config.js';
import { useRoomFiltering } from './hooks/use-room-filtering.js';
import { Problem } from './problem.jsx';

/** `<Confirm>` component. */
export const SelectStudio = (): ReactElement => {
  const params = useParams();

  const agentId = normalizeId<Agent['id']>(params.agentId);
  const requestId = normalizeId<LinkRequest['id']>(params.requestId);

  if (!agentId || !requestId) {
    throw new NotFoundError();
  }

  const [agent, setAgent] = useState<
    Omit<Agent, 'lastShutDownAt' | 'roomId' | 'status'> & LinkRequestLifetime
  >();

  const [selectedRoomId, setSelectedRoomId] = useState<RoomRecord['id']>();
  const [selectedOrgId, setSelectedOrgId] = useState<Uint8Array>();
  const [orgs, setOrgs] = useState<AgentRegistryGetOrganizationResponse[]>();

  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const lifetime = useLinkRequestLifetime(agent?.linkExpiresAt, error);
  const [isAtSourceLimit, setIsAtSourceLimit] = useState(false);
  const [isCheckingLimits, setIsCheckingLimits] = useState(false);
  const [planName, setPlanName] = useState<string>('your current');

  const limitsCacheRef = useRef<Map<RoomRecord['id'], { atLimit: boolean; planName: string }>>(
    new Map()
  );
  const currentCheckRoomIdRef = useRef<RoomRecord['id'] | null>(null);

  const { getAccessTokenSilently, user: auth0User } = useAuth0();
  const setRoomId = useBillingLimitMonitor();

  const navigate = useNavigate();
  const [accountInit, setAccountInit] = useState(false);

  // Get link flow configuration (feature flags)
  const config = useLinkFlowConfig();

  // Filter rooms based on organization selection and permissions
  const { filteredRooms } = useRoomFiltering({
    organizationId: config.enableOrganizationSelection ? selectedOrgId : undefined,
    additionalFilter: (room) => Boolean(room.permissions.canUpdate)
  });

  // Auto-select first organization if only one is available and org selection is enabled
  useEffect(() => {
    if (config.enableOrganizationSelection && orgs?.length === 1 && !selectedOrgId) {
      setSelectedOrgId(orgs[0]?.id);
    }
  }, [config.enableOrganizationSelection, orgs, selectedOrgId]);

  // Clear room selection when organization changes
  const handleOrganizationChange = (organizationId: Uint8Array | undefined): void => {
    setSelectedOrgId(organizationId);
    setSelectedRoomId(undefined);
    setIsAtSourceLimit(false);
  };

  // Monitor source limits when a room is selected
  useEffect(() => {
    if (!selectedRoomId) {
      setIsAtSourceLimit(false);
      setIsCheckingLimits(false);
      currentCheckRoomIdRef.current = null;
      return;
    }

    const cached = limitsCacheRef.current.get(selectedRoomId);
    if (cached !== undefined) {
      setIsAtSourceLimit(cached.atLimit);
      setPlanName(cached.planName);
      setIsCheckingLimits(false);
      return;
    }

    currentCheckRoomIdRef.current = selectedRoomId;

    const planMap: Record<BillingRegistryPlan, string> = {
      [BillingRegistryPlan.PLAN_FREE]: 'Free',
      [BillingRegistryPlan.PLAN_STANDARD]: 'Standard',
      [BillingRegistryPlan.PLAN_PRO]: 'Pro',
      [BillingRegistryPlan.PLAN_ENTERPRISE]: 'Enterprise',
      [BillingRegistryPlan.PLAN_CUSTOM]: 'Custom',
      [BillingRegistryPlan.PLAN_NFR]: 'NFR',
      // PLAN_UNKNOWN (=6) is now part of the in-repo billing_registry.proto the
      // frontend builds against (this unit). The Record<> type is exhaustive,
      // so the new enum key must be present.
      [BillingRegistryPlan.PLAN_UNKNOWN]: 'Unknown'
    };

    const checkLimits = async () => {
      const roomIdBeingChecked = selectedRoomId;
      setIsCheckingLimits(true);
      try {
        await setRoomId(roomIdBeingChecked);
        const token = await getAccessTokenSilently();

        const [canAdd, billingResp] = await Promise.all([
          canAddSourceToRoom(roomIdBeingChecked, token),
          BillingRegistryGetBillingInfo.request(
            nats.cloudConnection(),
            BillingRegistryGetRoomBillingInfoRequest.fromPartial({
              roomID: toIdFromHex(roomIdBeingChecked)!,
              token
            })
          )
        ]);

        if (currentCheckRoomIdRef.current !== roomIdBeingChecked) return;

        const atLimit = !canAdd;
        const resolvedPlanName = planMap[billingResp.plan] ?? 'your current';
        limitsCacheRef.current.set(roomIdBeingChecked, { atLimit, planName: resolvedPlanName });
        setIsAtSourceLimit(atLimit);
        setPlanName(resolvedPlanName);
      } catch (error) {
        logger.error('Failed to check room source limits', error);
        if (currentCheckRoomIdRef.current !== roomIdBeingChecked) return;

        setIsAtSourceLimit(false);
      } finally {
        if (currentCheckRoomIdRef.current === roomIdBeingChecked) {
          setIsCheckingLimits(false);
        }
      }
    };

    checkLimits();
  }, [selectedRoomId, setRoomId, getAccessTokenSilently]);

  useAsyncEffect(
    function* generator(signal: AbortSignal) {
      try {
        const token = yield* unwrap(getAccessTokenSilently());

        const response = yield* unwrap(fetchRequestLink(requestId, token));

        setAgent({ ...response, id: agentId });

        // Only fetch organizations if organization selection is enabled
        if (config.enableOrganizationSelection) {
          const listedOrgs = yield* unwrap(listOrganizations(token));
          setOrgs(listedOrgs);

          if (!listedOrgs.length) {
            // Use organization name from Studio user if available, otherwise fall back to Auth0 user name
            const organizationName = auth0User?.organizationData?.name;
            const fallbackName = auth0User?.name ?? auth0User?.email;
            yield* unwrap(initAccount(token, fallbackName, organizationName));
            setAccountInit(true);
          }
        }
      } catch (error_) {
        if (!signal.aborted) {
          setError(error_ as ApiError);
        }
      }
    },
    [requestId, config.enableOrganizationSelection, setAccountInit, accountInit]
  );

  const handleCreateStudio = useLatestFn((event?: React.MouseEvent) => {
    event?.preventDefault();
    if (lifetime.expired) return;

    navigate(Paths.agent.link.createStudio.resolve(agentId, requestId), {
      state: { shouldAutoCreate: filteredRooms?.length === 0 }
    });
  });

  const handleLink = useLatestFn(async () => {
    try {
      const token = await getAccessTokenSilently();
      assertLinkRequestActive(agent?.linkExpiresAt);
      await linkAgent(agentId, requestId, selectedRoomId!, token);
      // Force refresh of agents.
      await fetchAgentsForRoom(selectedRoomId!, await getAccessTokenSilently(), true);

      navigate(Paths.agent.link.configure.resolve(agentId, requestId));
    } catch (error_) {
      setBusy(false);
      setError(error_ as ApiError);
      throw error_;
    }
  });

  const handleEmbeddedBridgeLink = () => {
    navigate(Paths.agent.link.auth.resolve(agentId, requestId, selectedRoomId!));
  };

  const handleLinkAgent = useLatestFn(async () => {
    if (lifetime.expired) return;
    setBusy(true);

    if (agent?.embedded) {
      handleEmbeddedBridgeLink();
    } else {
      handleLink().catch(noop);
    }
  });

  // Auto-navigate to create studio when no rooms are available
  const hasNavigatedRef = useRef(false);
  useEffect(() => {
    if (!agent && !error && filteredRooms.length === 0 && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      handleCreateStudio();
    }
  }, [agent, error, filteredRooms.length, handleCreateStudio]);

  useRenderedBootHumanWait(!isBusy && !isCheckingLimits && (!!agent || !!error));

  // Let an already submitted claim settle; expiry prevents new actions.
  if (lifetime.expired && !isBusy) return <LinkRequestLifetimeNotice {...lifetime} />;

  // Show loader while agent data is being fetched
  if (!agent && !error) {
    return <Loader />;
  }

  const continueButtonText = agent?.embedded
    ? 'Continue'
    : isBusy
      ? `Linking ${agent?.embedded ? 'Camera' : 'Machine'}...`
      : `Link ${agent?.embedded ? 'Camera' : 'Machine'}`;

  return (
    <Wizard.Root currentStep={2} totalSteps={3}>
      <LinkRequestLifetimeNotice {...lifetime} />
      {!!agent && (
        <>
          <Hardware agent={agent} />
          <Wizard.Separator />
        </>
      )}

      {!!error && (
        <Problem agent={{ ...agent, id: agentId }} error={{ ...error, title: 'Linking Error' }}>
          <Anchor
            className='text-primary'
            onClick={(): void => {
              navigate(Paths.agent.link.confirm.resolve(agentId, requestId));
            }}
          >
            Try again
          </Anchor>
        </Problem>
      )}

      {!!agent && !error && (
        <>
          <Wizard.Verbiage
            message={`Select which Studio you would like to link with this ${
              agent.embedded ? 'Camera' : 'Computer'
            }.`}
            title={
              config.enableOrganizationSelection && orgs && orgs.length > 1
                ? 'Select Your Organization & Studio'
                : 'Select Your Studio'
            }
          />

          {config.enableOrganizationSelection && (
            <OrganizationSelector
              organizations={orgs ?? []}
              selectedOrganizationId={selectedOrgId ?? undefined}
              onOrganizationChange={handleOrganizationChange}
              show={config.enableOrganizationSelection}
            />
          )}

          {/* select studio */}
          <Select.Root
            onValueChange={(v: RoomRecord['id']): void => {
              setSelectedRoomId(v);
            }}
            size='xl'
            value={selectedRoomId ? selectedRoomId : undefined}
            variant='light'
          >
            <Select.Trigger
              className={selectedRoomId === undefined ? 'text-muted' : undefined}
              key={selectedRoomId === undefined ? 'trigger-placeholder' : 'trigger-active'}
            >
              <Select.Value placeholder='Select Studio' />
            </Select.Trigger>

            <Select.Content>
              {filteredRooms.map((room) => (
                <Select.Item key={room.id} value={room.id}>
                  {room.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>

          {isAtSourceLimit && (
            <Callout severity='error' title='Source Limit Reached' className='mb-4'>
              <div className='text-sm'>
                Camera limit reached on {planName} plan. Upgrade plan or link{' '}
                {agent?.embedded ? 'Camera' : 'Machine'} with another Studio.
              </div>
            </Callout>
          )}

          <Wizard.ButtonBox>
            <Wizard.Button
              busy={isBusy || isCheckingLimits}
              disabled={isBusy || isCheckingLimits || !selectedRoomId || isAtSourceLimit}
              onClick={handleLinkAgent}
            >
              {continueButtonText}
            </Wizard.Button>

            <Anchor className='text-primary' onClick={handleCreateStudio}>
              Or create a new Studio
            </Anchor>
          </Wizard.ButtonBox>
        </>
      )}
    </Wizard.Root>
  );
};
